import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormatAddressString } from "go-core/components/Formatters/FormatAddress";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as CircleInfoSVG } from "go-core/images/svg/circle-info.svg";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerApi } from "../../../../../../../../../../services/Api/Partner/partnerTypes";
import { api } from "../../../../../../../../../../services/Api/api";
import { OrganizationApi } from "../../../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../../../services/organizations/selectors";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import OrganizationPartnersProvisionModal from "../../../../../../../../components/Admin/Organization/components/OrganizationPartnersProvisionsModal";
import FormatSubscriptionLicenseType from "../../../../../../../../components/FormatSubscriptionLicenseType/FormatSubscriptionLicenseType";
import OrganizationListAlerts from "../../../../../../../../components/Organization/OrganizationListAlerts";
import OrganizationListApplicationsColumn from "../../../../../../../../components/Organization/OrganizationListApplicationsColumn";
import OrganizationListPartnersInfo from "../../../../../../../../components/Organization/OrganizationListPartnersInfo";
import { isPosnetPartner } from "../../../../../../utils";
import CreatePartnerOrganizationModal from "../../../../components/CreatePartnerOrganizationModal";

interface ListState {
	resource?: Record<string, any>;
	partnerId: number;
}

const listName = "PARTNER_ORGANIZATION";
const resourceType = "ORGANIZATION";

const List = ({ resource, partnerId }: ListState) => {
	const [items, setItems] = useState<OrganizationApi[]>([]);
	const [params, setParams] = useState<Record<string, any>>({});
	const { addFlash } = useFlash();
	const [showOrganizationModal, setShowOrganizationModal] = useState(false);
	const [showPartnersProvisionsModal, setShowPartnersProvisionsModal] = useState<number | undefined>(undefined);
	const partner = useSelector(selectPartner);
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const organization = useSelector(selectOrganization);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;
	const data = resource.read();

	const onHideModal = async (newOrganization?: OrganizationApi) => {
		setShowOrganizationModal(false);
		if (newOrganization) {
			try {
				const res = await partnerApi.getPartnerOrganizations(partnerId, params);
				setItems(res);
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
	};

	const getBarBackgroundWidth = (percentValue: number) => {
		if (percentValue > 100) return "100%";
		return `${percentValue}%`;
	};
	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: OrganizationApi) => (
					<div className="d-flex organization-name">
						<OrganizationListAlerts organization={item} />
						{!item.accept_rules && (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip id={`tooltip-${item.name}`}>
										{t("modules.organization.field.owner.title")}
									</Tooltip>
								}
							>
								<CircleInfoSVG
									className="me-1"
									style={{ minHeight: "16px", maxHeight: "16px", minWidth: "16px", maxWidth: "16px" }}
								/>
							</OverlayTrigger>
						)}
						<div className="d-flex flex-column gap-1">
							<RenderLimitedText minWidth={255} offsetWidth={8} forceApplyMinContentHeight>
								<Link to={`/partnership/${partnerId}/organizations/${item.id}`}>{item.name}</Link>
							</RenderLimitedText>
							<small>{item.company_id}</small>
							{item.stats && item.stats.feature_potential_count > 0 && (
								<>
									<div className="d-flex align-items-center justify-content-between gap-2">
										<div className="organization-product-chart-bar">
											<div
												className="organization-product-chart-bar-background"
												style={{ width: getBarBackgroundWidth(item.stats.feature_percent) }}
											/>
										</div>
										{item.stats.feature_potential_count > 0 ? (
											<p className="mb-0 font-weight-bold percent-value">
												{`${item.stats.feature_percent}%`}
												<small>
													({item.stats.feature_count}/{item.stats.feature_potential_count})
												</small>
											</p>
										) : (
											<p className="mb-0 font-weight-bold percent-value text-danger">Brak</p>
										)}
									</div>
								</>
							)}
						</div>
					</div>
				),
				renderExport: (item: OrganizationApi) => item.name,
			},
			{
				id: "contact_person",
				name: t("modules.organization.field.contact_person.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => {
					return (
						<div className="d-flex flex-column">
							{item.contact_person && <span>{item.contact_person}</span>}
							{item.company_phone_number && (
								<small className="text-muted font-weight-bold">{item.company_phone_number}</small>
							)}
							{item.company_email && (
								<small className="text-muted font-weight-bold">{item.company_email}</small>
							)}
						</div>
					);
				},
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				disableSorting: true,
				render: (item: PartnerApi) => {
					return (
						<div className={"d-flex flex-column"}>
							<span>{item.company_name}</span>
							<small className="text-muted font-weight-bold">
								{FormatAddressString({
									street: item.company_street,
									build_nr: item.company_build_nr,
									flat_nr: item.company_flat_nr,
									zip_code: item.company_zip_code,
									city: item.company_city,
								})}
							</small>
							{item.company_tax_id_no && (
								<small className="text-muted font-weight-bold">NIP: {item.company_tax_id_no}</small>
							)}
						</div>
					);
				},
				renderExport: (item: PartnerApi) => {
					return `${item.company_name ? item.company_name : ""} ${
						item.company_tax_id_no ? ` ${item.company_tax_id_no}` : ""
					}${item.company_tax_id_no || item.company_name ? ", " : ""} ${FormatAddressString({
						street: item.company_street,
						build_nr: item.company_build_nr,
						flat_nr: item.company_flat_nr,
						zip_code: item.company_zip_code,
						city: item.company_city,
					})}`;
				},
			},
			{
				id: "license_expires_at",
				name: t("modules.organization.field.license_expires_at.title"),
				type: "date",
			},
			{
				id: "license_type",
				name: t("common.word.license_type"),
				type: "list",
				options: {
					STANDARD: t("enums.licenses.STANDARD"),
					FUTURE: t("enums.licenses.FUTURE"),
					CANCELLED: t("enums.licenses.CANCELLED"),
					TRIAL: t("enums.licenses.TRIAL"),
					PAUSED: t("enums.licenses.PAUSED"),
					MANUAL: t("enums.licenses.MANUAL"),
					NONE: t("enums.licenses.NONE"),
				},
				render: (data: OrganizationApi) => {
					return (
						<>
							<FormatSubscriptionLicenseType license_type={data.license_type} />
							{data.license_expires_at && (
								<small>
									<br />
									{FormatDate(data.license_expires_at)}
								</small>
							)}
						</>
					);
				},
				renderExport: (data: OrganizationApi) => t(`enums.licenses.${data.license_type}`),
			},
			{
				id: "category",
				name: t("common.word.category"),
				type: "list",
				options: {
					CLUB: t("enums.organization_category.CLUB"),
					RESTAURANT: t("enums.organization_category.RESTAURANT"),
					CAFE: t("enums.organization_category.CAFE"),
					FOODTRUCK: t("enums.organization_category.FOODTRUCK"),
					PUB_BAR: t("enums.organization_category.PUB_BAR"),
					SUSHI: t("enums.organization_category.SUSHI"),
					SHOP: t("enums.organization_category.SHOP"),
					OTHERS: t("enums.organization_category.OTHERS"),
				},
				render: (data: OrganizationApi) => t(`enums.organization_category.${data.category}`),
			},
			{
				id: "applications",
				name: t("common.word.applications"),
				type: "list",
				options: {
					GOPOS: "GoPOS",
				},
				render: (data: OrganizationApi) => <OrganizationListApplicationsColumn organization={data} />,
				renderExport: (data: OrganizationApi) => {
					return data.applications?.map((app: { name: string }) => app.name).join(", ");
				},
			},
			{
				id: "partner",
				name: t("common.word.partners"),
				type: "search_select",
				render: (organization: OrganizationApi) => (
					<OrganizationListPartnersInfo
						organization={organization}
						setShowPartnersProvisionsModal={setShowPartnersProvisionsModal}
					/>
				),
				renderExport: (organization: OrganizationApi) =>
					organization.partners.map((partner) => partner.name).join(", "),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						partnerApi.getSubPartnersSearchSelect(partnerId, search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "cancelled_at",
				name: t("common.word.cancelled_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.cancelled_at),
			},
			{
				id: "mrr",
				name: t("common.word.mrr"),
				render: (item: OrganizationApi) => FormatMoney(item.mrr),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "mrr_sum",
				name: t("common.word.mrr_sum"),
				render: (item: OrganizationApi) => FormatMoney(item.mrr_sum),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "migrated_at",
				name: t("common.word.migrated_at"),
				render: (item: OrganizationApi) => FormatDate(item.migrated_at),
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at"),
				render: (item: OrganizationApi) => FormatDate(item.first_paid_at),
			},
			{
				id: "payment_date_due",
				name: t("common.word.payment_date_due"),
				render: (item: OrganizationApi) => FormatDate(item.payment_date_due),
			},
			{
				id: "not_paid_invoices",
				name: t("modules.organization.field.not_paid_invoices.title"),
				render: (item: OrganizationApi) =>
					`${item.not_paid_invoices_count} / ${FormatMoney(item.not_paid_invoices_amount)}`,
			},
			{
				id: "last_sale_at",
				name: t("common.word.last_sale_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.last_sale_at),
			},
			{
				id: "first_sale_at",
				name: t("common.word.first_sale_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.first_sale_at),
			},
			{
				id: "last_activity_at",
				name: t("common.word.last_activity_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.last_activity_at),
			},
			{
				id: "first_activity_at",
				name: t("common.word.first_activity_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.first_activity_at),
			},
			{
				id: "last_used_at",
				name: t("common.word.last_used_at"),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.last_used_at)?.split(",")[0],
			},
			{
				id: "cancellation_reason",
				name: t("common.word.cancellation_reason"),
				type: "text",
				render: (item: OrganizationApi) => item.cancellation_reason,
			},
			{
				id: "cancellation_reason_details",
				name: t("modules.organization.field.cancellation_reason_details.title"),
				type: "text",
				render: (item: OrganizationApi) => item.cancellation_reason_details,
			},
			{
				id: "abused_access",
				name: t("modules.organization.field.abused_access.title"),
				type: "number",
			},
			{
				id: "potential_products",
				name: t("modules.organization.field.potential_products.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.potential_products.map((product) => product).join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) => item.potential_products.map((product) => product).join(", "),
			},
			{
				id: "products",
				name: t("modules.organization.field.products.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.products.map((product) => product).join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) => item.products.map((product) => product).join(", "),
			},
			{
				id: "potential_points_count",
				name: t("modules.organization.field.potential_points_count.title"),
				type: "number",
			},
			{
				id: "points_count",
				name: t("modules.organization.field.points_count.title"),
				type: "number",
			},
			{
				id: "potential_products_count",
				name: t("modules.organization.field.potential_products_count.title"),
				type: "number",
			},
			{
				id: "products_count",
				name: t("modules.organization.field.products_count.title"),
				type: "number",
			},
			{
				id: "products_percent",
				name: t("modules.organization.field.products_percent.title"),
				type: "number",
			},
			{
				id: "points_percent",
				name: t("modules.organization.field.points_percent.title"),
				type: "number",
			},
		],
		filters: [
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
			},
			{
				id: "activated_at",
				name: t("common.word.activated_at"),
				type: "date",
			},
			{
				id: "license_expires_at",
				name: t("modules.organization.field.license_expires_at.title"),
				type: "date",
			},
			{
				id: "gopos_last_used_at",
				name: t("modules.organization.field.gopos_last_used_at.title"),
				type: "date",
			},
			{
				id: "gopos_created_at",
				name: t("modules.organization.field.gopos_created_at.title"),
				type: "date",
			},
			{
				id: "gopos_license_expires_at",
				name: t("modules.organization.field.gopos_license_expires_at.title"),
				type: "date",
			},
			{
				id: "gopos_activated_at",
				name: t("modules.organization.field.gopos_activated_at.title"),
				type: "date",
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				type: "text",
			},
			{
				id: "company_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "company_street",
				name: t("lib:common.address.street"),
				type: "text",
			},
			{
				id: "company_city",
				name: t("lib:common.address.city"),
				type: "text",
			},
			{
				id: "company_email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "company_phone_number",
				name: t("lib:common.word.phone_number"),
				type: "text",
			},
			{
				id: "terminal_count",
				name: t("modules.organization.field.terminal_count.title"),
				type: "number",
			},
			{
				id: "rules_accepted",
				name: t("modules.organization.field.rules_accepted.title"),
				type: "boolean",
			},
			{
				id: "not_paid_invoices_amount",
				name: t("modules.organization.field.not_paid_invoices_amount.title"),
				type: "number",
			},
			{
				id: "not_paid_invoices_count",
				name: t("modules.organization.field.not_paid_invoices_count.title"),
				type: "number",
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "days_past_payment_date_due",
				name: t("modules.organization.field.days_past_payment_date_due.title"),
				type: "number",
			},
			{
				id: "gopos_closed_pos_reports_count",
				name: t("modules.organization.field.gopos_closed_pos_reports_count.title"),
				type: "number",
			},
			{
				id: "gopos_last_closed_pos_report_at",
				name: t("modules.organization.field.gopos_last_closed_pos_report_at.title"),
				type: "date",
			},
			{
				id: "migrated_at",
				name: t("common.word.migrated_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "partner_type",
				name: t("common.word.partner_type"),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "potential_product",
				name: t("modules.organization.field.potential_product.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.getOrganizationProductSearchSelect(search, params, options),
				},
				withoutInfiniteScroll: true,
			},
			{
				id: "product",
				name: t("modules.organization.field.product.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.getOrganizationProductSearchSelect(search, params, options),
				},
				withoutInfiniteScroll: true,
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: OrganizationApi) => `/partnership/${partnerId}/organizations/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: OrganizationApi) => `/partnership/${partnerId}/organizations/${item.id}/edit`,
			},
			{
				name: `${t("lib:common.action.goto")}`,
				link: (item: OrganizationApi) => `/${item.id}`,
				visible: (item: OrganizationApi) => !item.accept_rules,
			},
		],
		selectedColumns: [
			"name",
			"category",
			"license_type",
			"applications",
			"partner",
			"company_name",
			"contact_person",
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
			{
				id: "rules_accepted",
				name: t("modules.organization.field.rules_accepted.title"),
				slug: "rules_accepted",
				filters: [
					{
						filterId: "rules_accepted",
						value: "false",
						condition: "false",
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params = {
				...params,
				include:
					"applications,applications.accesses,applications.properties,users,partners,custom_fields,stats",
			};
			setParams(params);
			return partnerApi.getPartnerOrganizations(partnerId, params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("lib:common.word.locations"),
			filename: t("modules.organization.field.export_config.filename"),
			organization: organization.name,
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		filterValues: data.filter_values,
	};

	const buttons: ButtonProps[] = [
		{
			title: `+ ${t("common.action.add", { ns: "lib" })}`,
			action: () => setShowOrganizationModal(true),
			variant: "primary",
			hide: isPosnetPartner(partner?.parent),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: `+ ${t("common.action.add", { ns: "lib" })}`,
			action: () => setShowOrganizationModal(true),
			hide: isPosnetPartner(partner?.parent),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("lib:common.word.locations")} buttons={buttons} />}
			{showOrganizationModal && <CreatePartnerOrganizationModal partnerId={partnerId} onHide={onHideModal} />}
			{showPartnersProvisionsModal && (
				<OrganizationPartnersProvisionModal
					isShown={showPartnersProvisionsModal}
					onHide={() => setShowPartnersProvisionsModal(undefined)}
					organizationId={showPartnersProvisionsModal}
					partnerId={partner?.id}
					isAdminMode={false}
				/>
			)}
			<div className="organizations-table">
				<ListData
					data={items}
					config={config}
					onFetch={(items: OrganizationApi[]) => setItems(items)}
					mobileActions={mobileActions}
				/>
			</div>
		</>
	);
};
const PartnerPreviewOrganizationsIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const partnerId = useSelector(selectPartner)?.id;
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("lib:common.word.locations")} | ${t("modules.partner.field.partner_panel.title")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List partnerId={Number(partnerId)} resource={resource} />
		</Suspense>
	);
};
export default PartnerPreviewOrganizationsIndexPage;
